// src/components/DeleteModal.js
// FIXED: was using `token` from useAuth() which AuthContext never exports
// AuthContext only exposes { user, login, logout } — token is not in context
// Fix: read token directly from localStorage (same pattern used everywhere else)

import { useEffect, useState } from "react";
import { IonModal, IonContent } from "@ionic/react";
import { Haptics, NotificationType } from "@capacitor/haptics";
import { useAuth } from "../Context/AuthContext";
import { apiFetch } from "../utils/api";

export default function DeleteModal() {
  const [open, setOpen]         = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const { logout } = useAuth();

  useEffect(function() {
    function handler() { setOpen(true); setError(""); setConfirmed(false); }
    window.addEventListener("open-delete", handler);
    return function() { window.removeEventListener("open-delete", handler); };
  }, []);

  async function handleDelete() {
    setLoading(true);
    setError("");
    try {
      // FIXED: read token from localStorage directly — AuthContext does not expose token
      const token = localStorage.getItem("gt_token") || sessionStorage.getItem("gt_token");
      if (!token) {
        setError("Not logged in — please refresh and try again");
        setLoading(false);
        return;
      }
      const res = await apiFetch("/api/delete-user", {
        method: "POST",
        headers: { "Authorization": "Bearer " + token }
      });
      const data = await res.json().catch(function() { return {}; });
      if (!res.ok) {
        setError(data.error || "Delete failed — please try again");
        setLoading(false);
        return;
      }
      // Success: clear auth and close
      await Haptics.notification({ type: NotificationType.Success }).catch(() => {});
      logout();
      setOpen(false);
      // Redirect home
      window.location.href = "/";
    } catch (e) {
      setError("Network error — please check your connection");
      setLoading(false);
    }
  }

  return (
    <IonModal isOpen={open} onDidDismiss={() => { setOpen(false); setConfirmed(false); }}
      breakpoints={[0, 1]} initialBreakpoint={1}
      style={{"--background": "#18181b", "--border-radius": "16px 16px 0 0"}}>
    <IonContent style={{"--background": "#18181b"}}>
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 mx-4 mt-6">
        <h3 className="text-white text-lg font-bold mb-2">Delete account?</h3>
        <p className="text-zinc-400 text-sm mb-1">This will permanently delete:</p>
        <ul className="text-zinc-500 text-xs mb-4 space-y-0.5 list-disc list-inside">
          <li>Your profile and all personal data</li>
          <li>All games you created</li>
          <li>All leagues you created</li>
          <li>Your stats, comments, and notifications</li>
        </ul>
        <p className="text-red-400 text-xs mb-5 font-semibold">This cannot be undone.</p>

        {!confirmed ? (
          <div className="space-y-2">
            <button onClick={function() { setConfirmed(true); }}
              className="w-full bg-red-600 hover:bg-red-500 text-white font-semibold rounded-lg py-2.5 text-sm transition-colors">
              Yes, delete my account
            </button>
            <button onClick={function() { setOpen(false); }}
              className="w-full bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg py-2.5 text-sm transition-colors">
              Cancel
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-red-400 text-xs font-semibold text-center mb-3">Are you absolutely sure? This is irreversible.</p>
            <button onClick={handleDelete} disabled={loading}
              className="w-full bg-red-700 hover:bg-red-600 disabled:opacity-50 text-white font-bold rounded-lg py-2.5 text-sm transition-colors">
              {loading ? "Deleting everything…" : "Delete permanently"}
            </button>
            <button onClick={function() { setConfirmed(false); setError(""); }}
              className="w-full bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg py-2.5 text-sm transition-colors">
              Go back
            </button>
          </div>
        )}

        {error && (
          <p className="mt-3 text-red-400 text-xs text-center">{error}</p>
        )}
      </div>
    </IonContent>
    </IonModal>
  );
}